import { remoteSupportWidgetConfig, RemoteSupportWidgetVariant } from '@/modules/common/config/remote-support-widget.config'

const SCRIPT_ELEMENT_ID = 'forethought-widget-embed-script'

export interface RemoteSupportWidgetUser {
  accountId: string;
  email: string;
  fullName: string;
}

/**
 * Controls the Forethought "solve-widget" support widget.
 *
 * Per Forethought's own docs, the widget only re-reads embed script attributes (and resets its
 * persisted conversation) "when a page ... is opened" - swapping the <script> tag's attributes
 * in an already-running SPA is a documented no-op. So once a widget instance is mounted for a
 * given event+variant+user combo, the only way to actually switch to a different combo is a
 * real page reload. Ordinary in-event navigation never hits that path - it just shows/hides the
 * existing instance via Forethought's window.Forethought('widget', 'show'/'hide') command API.
 *
 * config-ft-persistence-id is scoped per event+variant (Forethought's documented pattern for
 * isolating persisted conversations) so switching events never surfaces a different event's
 * conversation history.
 */
export default class RemoteSupportWidgetService {
  static mount (variant: RemoteSupportWidgetVariant, user: RemoteSupportWidgetUser, eventUuid: string): void {
    const config = remoteSupportWidgetConfig[variant]
    const workflowTag = config.staticDataAttributes['data-ft-workflow-tag']
    const persistenceId = `${eventUuid}-${workflowTag}`
    const existing = document.getElementById(SCRIPT_ELEMENT_ID)

    if (existing) {
      const isSameInstance =
        existing.getAttribute('data-ft-account-Id') === user.accountId &&
        existing.getAttribute('config-ft-persistence-id') === persistenceId
      if (isSameInstance) {
        this.show()
      } else {
        window.location.reload()
      }
      return
    }

    const script = document.createElement('script')
    script.id = config.id
    script.src = config.src
    const attributes: Record<string, string> = {
      ...config.staticDataAttributes,
      'data-ft-account-Id': user.accountId,
      'data-ft-Email': user.email,
      'data-ft-Full-Name': user.fullName,
      'config-ft-persistence-id': persistenceId
    }
    Object.entries(attributes).forEach(([name, value]) => {
      script.setAttribute(name, value)
    })
    document.head.appendChild(script)
  }

  static show (): void {
    this.callWidgetCommand('show')
  }

  static hide (): void {
    this.callWidgetCommand('hide')
  }

  static destroy (): void {
    const existing = document.getElementById(SCRIPT_ELEMENT_ID)
    if (existing) {
      existing.remove()
    }
  }

  private static callWidgetCommand (command: 'show' | 'hide'): void {
    const forethought = (window as any).Forethought
    if (typeof forethought === 'function') {
      forethought('widget', command)
    }
  }
}
